import { create } from 'zustand'
import type {
  AggregationFunctionAttribute,
  FilterOperatorAttribute,
  RecordAttributes,
} from '@features/records/recordAttributes'
import type {
  ResolvedAppDataTypeConfiguration,
  ResolvedConfiguration,
  ResolvedDayPartConfiguration,
  ResolvedModuleConfiguration,
} from '../models'
import type { AggregationType, FilterOperator } from '@features/views/models'
import {
  getDefaultDisplayFormatForAppDataTypeName,
  setMomentDisplayZones,
} from '../../columns/logic/displayFormats'
import { getUsFederalHolidayDatesForYear } from '@/config/usHolidays'
import type { HolidayDateKind } from '@/config/usHolidays'
import { readDeviceTheme } from '../logic/deviceTheme'
import { usePermissionBypassStore } from '@features/permissions/stores/usePermissionBypassStore'
import {
  browserTimeZone,
  DEFAULT_TIME_ZONE,
  isTimeZone,
  momentZones,
} from '@features/shared/utils/timeZones'

export type ThemeMode = 'light' | 'dark' | 'system'

/** When the navigation sidebar widens to show names beside its icons. */
export type SidebarNavigationMode = 'collapsed' | 'expanded' | 'hover'

/**
 * How an area's menu opens: as a card on hover, or pinned as a side panel.
 * A value this app no longer offers (`hover_shift`) reads as `hover`.
 */
export type SidebarMenuMode = 'hover' | 'pinned'

/** Recency means most recently opened first. */
export type SidebarTableSort = 'name' | 'recency' | 'custom'

export interface EmailGreetingBreakpoints {
  morningStart: number
  afternoonStart: number
  eveningStart: number
}

export interface AppDataTypeFormatConfiguration {
  defaultDisplayFormatId: string | null
  defaultDisplayFormatName?: string | null
  nullDisplayFormat?: string | null
  appDataTypeName?: string | null
  allowedFormats?: string[]
}

export interface HolidayCalendarConfiguration {
  source: 'us-federal' | 'custom'
  /** ISO dates, used when `source` is `custom`. */
  customDates: string[]
}

/** Minutes since midnight, both inclusive. */
export interface WorkingHoursConfiguration {
  startMinutes: number
  endMinutes: number
}

export interface SettingsValueMap {
  themeSetting: ThemeMode
  /** The address Home opens; null opens the app's own home. */
  homePage: string | null
  sidebarNavigationMode: SidebarNavigationMode
  sidebarMenuMode: SidebarMenuMode
  sidebarTableSort: SidebarTableSort
  sidebarTableFolders: boolean
  /** The Groups panel shows every way to sort groups, not just a level's order. */
  advancedGroupSorting: boolean
  /** A finished export downloads by itself, anywhere in Arvoke. */
  exportDownloadAutomatically: boolean
  /** The nearest Export menu opens when an export finishes. */
  exportOpenWhenReady: boolean
  /** How the Favorites menu orders favorites; Custom is the order added. */
  sidebarFavoritesSort: SidebarTableSort
  emailGreetingBreakpoints: EmailGreetingBreakpoints
  defaultEmailSignOff: string
  appDataTypeFormats: Record<string, AppDataTypeFormatConfiguration>
  holidayCalendar: HolidayCalendarConfiguration
  workingHours: WorkingHoursConfiguration
  /** The organization's zone: Primary Time Zone's default (Preference Defaults). */
  organizationTimeZone: string
  /** The IANA zone the person usually works in (Primary Time Zone): moments show in it. */
  primaryTimeZone: string
}

export type SettingKey = keyof SettingsValueMap
export type EffectiveSettings = SettingsValueMap

export interface SettingOption<T> {
  label: string
  value: T
}

export interface PreferenceDefinition<K extends SettingKey = SettingKey> {
  key: K
  label: string
  description: string
  systemDefault: SettingsValueMap[K]
  options?: readonly SettingOption<SettingsValueMap[K]>[]
}

export type PreferenceDefinitions = {
  [K in SettingKey]: PreferenceDefinition<K>
}

export interface RecordAttributeLookups {
  loaded: boolean
  appDataTypesById: Record<string, { id: string; name: string }>
  filterOperatorsById: Record<string, FilterOperatorAttribute>
  aggregationFunctionsById: Record<string, AggregationFunctionAttribute>
  filterOperatorIdsByAppDataTypeId: Record<string, string[]>
  aggregationFunctionIdsByAppDataTypeId: Record<string, string[]>
}

const DEFAULT_EMAIL_GREETING_BREAKPOINTS: EmailGreetingBreakpoints = {
  morningStart: 3 * 60,
  afternoonStart: 12 * 60,
  eveningStart: 18 * 60,
}
const DEFAULT_HOLIDAY_CALENDAR: HolidayCalendarConfiguration = {
  source: 'us-federal',
  customDates: [],
}
const DEFAULT_WORKING_HOURS: WorkingHoursConfiguration = {
  startMinutes: 8 * 60,
  endMinutes: 17 * 60,
}
// Configuration used to be cached under this key (and `<key>:<identity>`).
// The app waits for each session's own /configuration before it renders, so
// the copies were never shown; they are only cleared now.
const RESOLVED_CONFIGURATION_CACHE_KEY = 'arvoke.resolvedConfiguration.v1'

export const SETTING_DEFINITIONS: PreferenceDefinitions = {
  themeSetting: {
    key: 'themeSetting',
    label: 'Theme',
    description: 'Choose how Arvoke appears.',
    systemDefault: 'system',
    options: [
      { label: 'Light', value: 'light' },
      { label: 'Dark', value: 'dark' },
      { label: 'System', value: 'system' },
    ],
  },
  sidebarNavigationMode: {
    key: 'sidebarNavigationMode',
    label: 'Sidebar expansion',
    description: 'When the navigation sidebar widens to show names beside its icons.',
    systemDefault: 'hover',
    options: [
      { label: 'Collapsed', value: 'collapsed' },
      { label: 'Expanded', value: 'expanded' },
      // Widens while the pointer or keyboard focus is on it.
      { label: 'Dynamic', value: 'hover' },
    ],
  },
  sidebarMenuMode: {
    key: 'sidebarMenuMode',
    label: 'Pin sidebar sub-menu',
    description: "How an area's menu, such as the Tables list, opens beside the sidebar.",
    systemDefault: 'hover',
    options: [
      { label: 'Opens on hover', value: 'hover' },
      { label: 'Always open', value: 'pinned' },
    ],
  },
  sidebarTableSort: {
    key: 'sidebarTableSort',
    label: 'Sort method for tables',
    description: 'How tables are ordered in the sidebar.',
    systemDefault: 'name',
    options: [
      { label: 'Name', value: 'name' },
      { label: 'Recency', value: 'recency' },
      { label: 'Custom', value: 'custom' },
    ],
  },
  sidebarFavoritesSort: {
    key: 'sidebarFavoritesSort',
    label: 'Sort method for favorites',
    description: 'How favorites are ordered in the sidebar.',
    systemDefault: 'custom',
    options: [
      { label: 'Name', value: 'name' },
      { label: 'Recency', value: 'recency' },
      { label: 'Custom', value: 'custom' },
    ],
  },
  sidebarTableFolders: {
    key: 'sidebarTableFolders',
    label: 'Show table folders',
    description: 'Group sidebar tables into your folders.',
    systemDefault: true,
  },
  advancedGroupSorting: {
    key: 'advancedGroupSorting',
    label: 'Advanced Group Sorting',
    description: 'Show every way to sort groups in the Groups panel.',
    systemDefault: true,
  },
  exportDownloadAutomatically: {
    key: 'exportDownloadAutomatically',
    label: 'Download Automatically',
    description: 'Download a finished export by itself, wherever you are in Arvoke.',
    systemDefault: true,
  },
  exportOpenWhenReady: {
    key: 'exportOpenWhenReady',
    label: 'Open When Ready',
    description: 'Open the nearest Export menu when an export finishes.',
    systemDefault: false,
  },
  homePage: {
    key: 'homePage',
    label: 'Set homepage to',
    description: 'The page Home and the logo open.',
    // The AI Assistant (Path.aiChat), the catalog's default since migration 0030.
    systemDefault: '/ai',
  },
  emailGreetingBreakpoints: {
    key: 'emailGreetingBreakpoints',
    label: 'Parts of Day Breakpoints',
    description: 'Times used to choose morning, afternoon, or evening in generated emails.',
    systemDefault: DEFAULT_EMAIL_GREETING_BREAKPOINTS,
  },
  defaultEmailSignOff: {
    key: 'defaultEmailSignOff',
    label: 'Default Email Signoff',
    description: 'Signoff appended to generated share emails.',
    systemDefault: 'Thank you,',
  },
  appDataTypeFormats: {
    key: 'appDataTypeFormats',
    label: 'Data Type Formats',
    description: 'Default display formats for each app data type.',
    systemDefault: {},
  },
  holidayCalendar: {
    key: 'holidayCalendar',
    label: 'Holiday Calendar',
    description: 'Holiday dates used for work-day and holiday scheduling.',
    systemDefault: DEFAULT_HOLIDAY_CALENDAR,
  },
  workingHours: {
    key: 'workingHours',
    label: 'Working Hours',
    description: 'Daily working hours used for working-hours scheduling windows.',
    systemDefault: DEFAULT_WORKING_HOURS,
  },
  organizationTimeZone: {
    key: 'organizationTimeZone',
    label: 'Organization Time Zone',
    description: "Primary Time Zone's default: the zone new schedules start in.",
    systemDefault: DEFAULT_TIME_ZONE,
  },
  primaryTimeZone: {
    key: 'primaryTimeZone',
    label: 'Primary Time Zone',
    description: 'The time zone you usually work in. Times show in it.',
    systemDefault: DEFAULT_TIME_ZONE,
  },
}

function cloneGreetingBreakpoints(value: EmailGreetingBreakpoints): EmailGreetingBreakpoints {
  return { ...value }
}

function cloneHolidayCalendar(value: HolidayCalendarConfiguration): HolidayCalendarConfiguration {
  return { source: value.source, customDates: [...value.customDates] }
}

function createInitialEffectiveSettings(): EffectiveSettings {
  return {
    // Before a session's settings arrive (signed out, or between identities),
    // the theme is this device's.
    themeSetting: readDeviceTheme(),
    homePage: SETTING_DEFINITIONS.homePage.systemDefault,
    sidebarNavigationMode: SETTING_DEFINITIONS.sidebarNavigationMode.systemDefault,
    sidebarMenuMode: SETTING_DEFINITIONS.sidebarMenuMode.systemDefault,
    sidebarTableSort: SETTING_DEFINITIONS.sidebarTableSort.systemDefault,
    sidebarTableFolders: SETTING_DEFINITIONS.sidebarTableFolders.systemDefault,
    advancedGroupSorting: SETTING_DEFINITIONS.advancedGroupSorting.systemDefault,
    exportDownloadAutomatically: SETTING_DEFINITIONS.exportDownloadAutomatically.systemDefault,
    exportOpenWhenReady: SETTING_DEFINITIONS.exportOpenWhenReady.systemDefault,
    sidebarFavoritesSort: SETTING_DEFINITIONS.sidebarFavoritesSort.systemDefault,
    emailGreetingBreakpoints: cloneGreetingBreakpoints(
      SETTING_DEFINITIONS.emailGreetingBreakpoints.systemDefault
    ),
    defaultEmailSignOff: SETTING_DEFINITIONS.defaultEmailSignOff.systemDefault,
    appDataTypeFormats: {},
    holidayCalendar: cloneHolidayCalendar(DEFAULT_HOLIDAY_CALENDAR),
    workingHours: { ...DEFAULT_WORKING_HOURS },
    organizationTimeZone: SETTING_DEFINITIONS.organizationTimeZone.systemDefault,
    primaryTimeZone: SETTING_DEFINITIONS.primaryTimeZone.systemDefault,
  }
}

// Never unset: a zone this browser doesn't know reads as the organization's,
// and the organization's as the catalog's default.
function coerceTimeZone(value: unknown, fallback: string): string {
  return isTimeZone(value) ? value : fallback
}

function coerceTheme(value: unknown): ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system'
}

export function isSidebarNavigationMode(value: unknown): value is SidebarNavigationMode {
  return value === 'collapsed' || value === 'expanded' || value === 'hover'
}

function coerceSidebarNavigationMode(value: unknown): SidebarNavigationMode {
  return isSidebarNavigationMode(value)
    ? value
    : SETTING_DEFINITIONS.sidebarNavigationMode.systemDefault
}

/** An address inside the app, as Home Page stores it. */
export function isHomePage(value: unknown): value is string {
  return typeof value === 'string' && /^\/[^\s]*$/.test(value)
}

// Never unset: anything that isn't an address in the app reads as the default.
function coerceHomePage(value: unknown): string | null {
  return isHomePage(value) ? value : SETTING_DEFINITIONS.homePage.systemDefault
}

export function isSidebarMenuMode(value: unknown): value is SidebarMenuMode {
  return value === 'hover' || value === 'pinned'
}

function coerceSidebarMenuMode(value: unknown): SidebarMenuMode {
  return isSidebarMenuMode(value) ? value : SETTING_DEFINITIONS.sidebarMenuMode.systemDefault
}

export function isSidebarTableSort(value: unknown): value is SidebarTableSort {
  return value === 'name' || value === 'recency' || value === 'custom'
}

function coerceSidebarTableSort(value: unknown): SidebarTableSort {
  return isSidebarTableSort(value) ? value : SETTING_DEFINITIONS.sidebarTableSort.systemDefault
}

function coerceSidebarFavoritesSort(value: unknown): SidebarTableSort {
  return isSidebarTableSort(value) ? value : SETTING_DEFINITIONS.sidebarFavoritesSort.systemDefault
}

function coerceSidebarTableFolders(value: unknown): boolean {
  return typeof value === 'boolean' ? value : SETTING_DEFINITIONS.sidebarTableFolders.systemDefault
}

function coerceAdvancedGroupSorting(value: unknown): boolean {
  return typeof value === 'boolean' ? value : SETTING_DEFINITIONS.advancedGroupSorting.systemDefault
}

function coerceBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function coerceGreetingBreakpoints(value: unknown): EmailGreetingBreakpoints {
  return (
    coerceGreetingBreakpointsOrNull(value) ??
    cloneGreetingBreakpoints(DEFAULT_EMAIL_GREETING_BREAKPOINTS)
  )
}

function coerceGreetingBreakpointsOrNull(value: unknown): EmailGreetingBreakpoints | null {
  if (typeof value !== 'object' || value === null) return null

  const candidate = value as Partial<Record<keyof EmailGreetingBreakpoints, unknown>>
  const morningStart = Number(candidate.morningStart)
  const afternoonStart = Number(candidate.afternoonStart)
  const eveningStart = Number(candidate.eveningStart)

  if (
    !Number.isFinite(morningStart) ||
    !Number.isFinite(afternoonStart) ||
    !Number.isFinite(eveningStart)
  ) {
    return null
  }

  return { morningStart, afternoonStart, eveningStart }
}

function timeValueToMinutes(value: unknown): number | null {
  if (typeof value !== 'string') return null
  const [hourPart, minutePart] = value.split(':')
  const hour = Number(hourPart)
  const minute = Number(minutePart)
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null
  }
  return hour * 60 + minute
}

function dayPartBreakpointKey(label: unknown): keyof EmailGreetingBreakpoints | null {
  if (typeof label !== 'string') return null
  const normalized = label.trim().toLowerCase()
  if (normalized === 'morning') return 'morningStart'
  if (normalized === 'afternoon') return 'afternoonStart'
  if (normalized === 'evening') return 'eveningStart'
  return null
}

function coerceGreetingBreakpointsFromDayParts(value: unknown): EmailGreetingBreakpoints | null {
  if (typeof value !== 'object' || value === null) return null

  const breakpoints = cloneGreetingBreakpoints(DEFAULT_EMAIL_GREETING_BREAKPOINTS)
  let hasRows = false
  for (const row of Object.values(value as Record<string, ResolvedDayPartConfiguration>)) {
    if (typeof row !== 'object' || row === null) continue
    const key = dayPartBreakpointKey(row.day_part_name)
    const minutes = timeValueToMinutes(row.day_part_start_time)
    if (!key || minutes === null) continue
    breakpoints[key] = minutes
    hasRows = true
  }

  return hasRows ? breakpoints : null
}

function coerceSignOff(value: unknown): string {
  return typeof value === 'string' ? value : SETTING_DEFINITIONS.defaultEmailSignOff.systemDefault
}

function coerceAppDataTypeFormats(value: unknown): Record<string, AppDataTypeFormatConfiguration> {
  if (typeof value !== 'object' || value === null) return {}

  return Object.fromEntries(
    Object.entries(value as Record<string, ResolvedAppDataTypeConfiguration>).map(
      ([appDataTypeId, config]) => {
        const appDataTypeName =
          typeof config?.app_data_type_name === 'string' ? config.app_data_type_name : null
        const fallbackDefaultFormat = getDefaultDisplayFormatForAppDataTypeName(appDataTypeName)

        return [
          appDataTypeId,
          {
            appDataTypeName,
            defaultDisplayFormatId:
              typeof config?.default_display_format_id === 'string'
                ? config.default_display_format_id
                : (fallbackDefaultFormat?.id ?? null),
            defaultDisplayFormatName:
              typeof config?.default_display_format_name === 'string'
                ? config.default_display_format_name
                : (fallbackDefaultFormat?.name ?? null),
            nullDisplayFormat:
              typeof config?.null_display_format === 'string' ? config.null_display_format : null,
            allowedFormats: Array.isArray(config?.allowed_formats)
              ? config.allowed_formats.filter(
                  (format): format is string => typeof format === 'string'
                )
              : [],
          },
        ]
      }
    )
  )
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/** Tenant holidays come from a `holidays` settings table; without one, US federal holidays. */
function coerceHolidayCalendar(settings: unknown): HolidayCalendarConfiguration {
  if (typeof settings === 'object' && settings !== null) {
    const rows = (settings as Record<string, unknown>).holidays
    if (typeof rows === 'object' && rows !== null) {
      const customDates = Object.values(rows as Record<string, Record<string, unknown>>)
        .map((row) => row?.holiday_date)
        .filter((date): date is string => typeof date === 'string' && ISO_DATE_PATTERN.test(date))
        .sort()
      if (customDates.length > 0) return { source: 'custom', customDates }
    }
  }
  return cloneHolidayCalendar(DEFAULT_HOLIDAY_CALENDAR)
}

/** A `working_hours` setting with 24h `start`/`end` HH:mm strings; 8:00 to 17:00 without one. */
function coerceWorkingHours(settings: unknown): WorkingHoursConfiguration {
  if (typeof settings === 'object' && settings !== null) {
    const value = (settings as Record<string, unknown>).working_hours
    if (typeof value === 'object' && value !== null) {
      const candidate = value as Record<string, unknown>
      const startMinutes = timeValueToMinutes(candidate.start)
      const endMinutes = timeValueToMinutes(candidate.end)
      if (startMinutes !== null && endMinutes !== null && startMinutes < endMinutes) {
        return { startMinutes, endMinutes }
      }
    }
  }
  return { ...DEFAULT_WORKING_HOURS }
}

function effectiveSettingsFromConfiguration(
  configuration: ResolvedConfiguration
): EffectiveSettings {
  const appDataTypes = configuration.app_data_types ?? configuration.preferences.app_data_types
  const organizationTimeZone = coerceTimeZone(
    configuration.preference_defaults?.primary_time_zone,
    SETTING_DEFINITIONS.organizationTimeZone.systemDefault
  )

  return {
    themeSetting: coerceTheme(configuration.preferences.theme),
    homePage: coerceHomePage(configuration.preferences.home_page),
    sidebarNavigationMode: coerceSidebarNavigationMode(
      configuration.preferences.sidebar_navigation_mode
    ),
    sidebarMenuMode: coerceSidebarMenuMode(configuration.preferences.sidebar_menu_mode),
    sidebarTableSort: coerceSidebarTableSort(configuration.preferences.sidebar_table_sort),
    sidebarTableFolders: coerceSidebarTableFolders(configuration.preferences.sidebar_table_folders),
    advancedGroupSorting: coerceAdvancedGroupSorting(
      configuration.preferences.advanced_group_sorting
    ),
    exportDownloadAutomatically: coerceBoolean(
      configuration.preferences.export_download_automatically,
      SETTING_DEFINITIONS.exportDownloadAutomatically.systemDefault
    ),
    exportOpenWhenReady: coerceBoolean(
      configuration.preferences.export_open_when_ready,
      SETTING_DEFINITIONS.exportOpenWhenReady.systemDefault
    ),
    sidebarFavoritesSort: coerceSidebarFavoritesSort(
      configuration.preferences.sidebar_favorites_sort
    ),
    emailGreetingBreakpoints:
      coerceGreetingBreakpointsFromDayParts(configuration.preferences.day_parts) ??
      coerceGreetingBreakpoints(configuration.preferences.email_greeting_breakpoints),
    defaultEmailSignOff: coerceSignOff(configuration.preferences.default_email_signoff),
    appDataTypeFormats: coerceAppDataTypeFormats(appDataTypes),
    holidayCalendar: coerceHolidayCalendar(configuration.settings),
    workingHours: coerceWorkingHours(configuration.settings),
    organizationTimeZone,
    primaryTimeZone: coerceTimeZone(
      configuration.preferences.primary_time_zone,
      organizationTimeZone
    ),
  }
}

/** Removes the configuration copies earlier versions left in this browser, one per sign-in. */
export function clearCachedConfigurations() {
  if (typeof window === 'undefined') return
  try {
    const storage = window.localStorage
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index)
      if (
        key === RESOLVED_CONFIGURATION_CACHE_KEY ||
        key?.startsWith(`${RESOLVED_CONFIGURATION_CACHE_KEY}:`)
      ) {
        storage.removeItem(key)
      }
    }
  } catch {
    /* Storage may be unavailable. */
  }
}

export type ResolvedModules = Record<string, ResolvedModuleConfiguration>

/** Slugs of the enabled modules, or `null` while the module catalog is unknown. */
export function getEnabledModuleSlugs(modules: ResolvedModules | null): ReadonlySet<string> | null {
  if (modules === null) return null

  return new Set(
    Object.values(modules)
      .filter((module) => module.is_enabled)
      .map((module) => module.slug)
  )
}

function createInitialSettingsState() {
  return {
    ...createInitialEffectiveSettings(),
    appUserId: null,
    isAdministrator: false,
    configurationLoaded: false,
    settings: {},
    modules: null,
    systemTables: {},
  }
}

function createEmptyRecordAttributeLookups(): RecordAttributeLookups {
  return {
    loaded: false,
    appDataTypesById: {},
    filterOperatorsById: {},
    aggregationFunctionsById: {},
    filterOperatorIdsByAppDataTypeId: {},
    aggregationFunctionIdsByAppDataTypeId: {},
  }
}

function mapRecordAttributes(attributes: RecordAttributes): RecordAttributeLookups {
  return {
    loaded: true,
    appDataTypesById: Object.fromEntries(
      attributes.app_data_types.map((appDataType) => [appDataType.id, appDataType])
    ),
    filterOperatorsById: Object.fromEntries(
      attributes.filter_operators.map((operator) => [operator.id, operator])
    ),
    aggregationFunctionsById: Object.fromEntries(
      attributes.aggregation_functions.map((fn) => [fn.id, fn])
    ),
    filterOperatorIdsByAppDataTypeId: attributes.app_data_type_allowed_filters.reduce<
      Record<string, string[]>
    >((acc, item) => {
      acc[item.app_data_type_id] = [...(acc[item.app_data_type_id] ?? []), item.filter_operator_id]
      return acc
    }, {}),
    aggregationFunctionIdsByAppDataTypeId: attributes.app_data_type_allowed_aggregations.reduce<
      Record<string, string[]>
    >((acc, item) => {
      acc[item.app_data_type_id] = [
        ...(acc[item.app_data_type_id] ?? []),
        item.aggregation_function_id,
      ]
      return acc
    }, {}),
  }
}

export interface SettingsState extends EffectiveSettings {
  appUserId: string | null
  /** Manages permissions, and may bypass them (the Me menu's Bypass Permissions). */
  isAdministrator: boolean
  configurationLoaded: boolean
  settings: Record<string, unknown>
  /** Resolved product modules keyed by slug; `null` until the catalog is known. */
  modules: ResolvedModules | null
  /** System metadata table ids keyed `schema.table`, e.g. `meta.columns`. */
  systemTables: Record<string, string>
  recordAttributes: RecordAttributeLookups
  hydrateConfiguration: (configuration: ResolvedConfiguration) => void
  applyEffectiveSettings: (settings: Partial<EffectiveSettings>) => void
  getSettingDefinitions: () => PreferenceDefinition[]
  getHolidayDatesForYear: (year: number, kind?: HolidayDateKind) => string[]
  setRecordAttributes: (attributes: RecordAttributes) => void
  getFilterOperatorsForAppDataType: (
    appDataTypeId: string | null | undefined
  ) => FilterOperatorAttribute[]
  getAggregationFunctionsForAppDataType: (
    appDataTypeId: string | null | undefined
  ) => AggregationFunctionAttribute[]
  isFilterOperatorAllowedForAppDataType: (
    appDataTypeId: string | null | undefined,
    operator: FilterOperator
  ) => boolean
  isAggregationFunctionAllowedForAppDataType: (
    appDataTypeId: string | null | undefined,
    fn: AggregationType
  ) => boolean
  getFilterOperatorRequiresValue: (
    operator: FilterOperator,
    appDataTypeId?: string | null
  ) => boolean | null
  getAggregationFunctionLabel: (fn: AggregationType, appDataTypeId?: string | null) => string | null
  resetSettings: () => void
}

export const useSettingsStore = create<SettingsState>()((set, get) => ({
  ...createInitialSettingsState(),
  recordAttributes: createEmptyRecordAttributeLookups(),

  hydrateConfiguration: (configuration) => {
    // Only an administrator may bypass permissions; anyone else's switch is off.
    if (!configuration.is_administrator) usePermissionBypassStore.getState().setBypassing(false)
    set(() => ({
      ...effectiveSettingsFromConfiguration(configuration),
      appUserId: configuration.app_user_id ?? null,
      isAdministrator: configuration.is_administrator ?? false,
      configurationLoaded: true,
      settings: configuration.settings,
      modules: configuration.modules ?? null,
      systemTables: configuration.system_tables ?? {},
    }))
  },
  applyEffectiveSettings: (settings) =>
    set((state) => ({
      ...settings,
      configurationLoaded: state.configurationLoaded || Object.keys(settings).length > 0,
    })),
  getSettingDefinitions: () => Object.values(SETTING_DEFINITIONS),
  getHolidayDatesForYear: (year, kind = 'observed') => {
    const calendar = get().holidayCalendar
    if (calendar.source === 'custom') {
      // Tenant dates carry no observance, so they are both the observed and calendar set.
      return calendar.customDates.filter((date) => date.startsWith(`${year}-`))
    }
    return getUsFederalHolidayDatesForYear(year, kind)
  },
  setRecordAttributes: (attributes) => set({ recordAttributes: mapRecordAttributes(attributes) }),
  getFilterOperatorsForAppDataType: (appDataTypeId) => {
    if (!appDataTypeId) return []

    const attributes = get().recordAttributes
    return (attributes.filterOperatorIdsByAppDataTypeId[appDataTypeId] ?? []).flatMap((id) => {
      const operator = attributes.filterOperatorsById[id]
      return operator ? [operator] : []
    })
  },
  getAggregationFunctionsForAppDataType: (appDataTypeId) => {
    if (!appDataTypeId) return []

    const attributes = get().recordAttributes
    return (attributes.aggregationFunctionIdsByAppDataTypeId[appDataTypeId] ?? []).flatMap((id) => {
      const fn = attributes.aggregationFunctionsById[id]
      return fn ? [fn] : []
    })
  },
  isFilterOperatorAllowedForAppDataType: (appDataTypeId, operator) => {
    const attributes = get().recordAttributes
    if (!attributes.loaded || !appDataTypeId) return false

    return get()
      .getFilterOperatorsForAppDataType(appDataTypeId)
      .some((allowed) => allowed.key === operator)
  },
  isAggregationFunctionAllowedForAppDataType: (appDataTypeId, fn) => {
    const attributes = get().recordAttributes
    if (!attributes.loaded || !appDataTypeId) return false

    return get()
      .getAggregationFunctionsForAppDataType(appDataTypeId)
      .some((allowed) => allowed.key === fn)
  },
  getFilterOperatorRequiresValue: (operator, appDataTypeId) => {
    const candidates = appDataTypeId
      ? get().getFilterOperatorsForAppDataType(appDataTypeId)
      : Object.values(get().recordAttributes.filterOperatorsById)

    return candidates.find((candidate) => candidate.key === operator)?.requires_value ?? null
  },
  getAggregationFunctionLabel: (fn, appDataTypeId) => {
    const candidates = appDataTypeId
      ? get().getAggregationFunctionsForAppDataType(appDataTypeId)
      : Object.values(get().recordAttributes.aggregationFunctionsById)

    return candidates.find((candidate) => candidate.key === fn)?.name ?? null
  },
  resetSettings: () => {
    usePermissionBypassStore.getState().setBypassing(false)
    set({
      ...createInitialEffectiveSettings(),
      appUserId: null,
      isAdministrator: false,
      configurationLoaded: false,
      settings: {},
      modules: null,
      systemTables: {},
      recordAttributes: createEmptyRecordAttributeLookups(),
    })
  },
}))

// Grid cells format moments outside React (`formatColumnValue`), so the zones
// they show in follow the store: the viewer's Primary Time Zone and, when
// that isn't where their browser is, the browser's. Until a session's
// settings arrive, the browser's clock.
function syncMomentDisplayZones(state: SettingsState) {
  setMomentDisplayZones(
    state.configurationLoaded ? momentZones(state.primaryTimeZone, browserTimeZone()) : null
  )
}

useSettingsStore.subscribe((state, previous) => {
  if (
    state.configurationLoaded !== previous.configurationLoaded ||
    state.primaryTimeZone !== previous.primaryTimeZone
  ) {
    syncMomentDisplayZones(state)
  }
})
